import {Component, effect, inject, input, output, signal, computed, viewChild, ElementRef, ChangeDetectionStrategy} from "@angular/core"
import type {CmLogger} from "@cm/log"
import {CM_LOGGER_TEXTURES} from "@common/services/logging"
import {
    CreateInitialTextureSetRevisionGQL,
    QueryInitialTextureRevisionsGQL,
    QueryLatestTextureSetRevisionIdGQL,
    QueryTextureSetJobsGQL,
    QueryTextureSetViewDataGQL,
    TextureSetJobFragment,
    TextureSetViewDataFragment,
    TextureSetViewRevisionDataFragment,
    UpdateTextureSetNameGQL,
} from "@app/textures/texture-set/texture-set.generated"
import {getSimpleJobState, SimpleJobState} from "@app/textures/utils/simple-job-state"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {mutateThrowingErrors} from "@common/helpers/api/mutate"
import {AuthService} from "@common/services/auth/auth.service"
import {UtilsService} from "@legacy/helpers/utils"
import {RouterLink} from "@angular/router"
import {InputDirective} from "@app/common/directives"
import {NotificationsService} from "@common/services/notifications/notifications.service"
import {TextureType} from "@generated"
import {IsDefined} from "@cm/utils/filter"
import {RefreshService} from "@app/common/services/refresh/refresh.service"
import {filter} from "rxjs"
import {PermissionsService} from "@app/common/services/permissions/permissions.service"
import {MatTooltipModule} from "@angular/material/tooltip"
import {TextureSetRevisionViewComponent} from "app/textures/texture-set-revision-view/texture-set-revision-view.component"
import {ButtonComponent} from "@common/components/buttons/button/button.component"
import {MatMenuModule} from "@angular/material/menu"
import {CommonModule} from "@angular/common"
import {getRevisionTitle} from "@app/textures/utils/texture-set-revision-utils"
import {TexturesApiService} from "@app/textures/service/textures-api.service"
import {MatDialog} from "@angular/material/dialog"
import {JobTaskProgressComponent} from "@common/components/job-task-progress/job-task-progress.component"
import {MaterialAiDialogComponent} from "@app/platform/components/materials/material-ai-dialog/material-ai-dialog.component"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-texture-set",
    templateUrl: "./texture-set.component.html",
    styleUrls: ["./texture-set.component.scss"],
    imports: [
        CommonModule,
        InputDirective,
        RouterLink,
        TextureSetRevisionViewComponent,
        MatTooltipModule,
        MatMenuModule,
        ButtonComponent,
        JobTaskProgressComponent,
    ],
})
export class TextureSetComponent {
    readonly $textureSetId = input<string | undefined>(undefined, {alias: "textureSetId"})
    readonly $canEdit = input<boolean>(true, {alias: "canEdit"})
    readonly $canDownload = input<boolean>(true, {alias: "canDownload"})

    readonly jobSubmitted = output<void>()

    readonly permission = inject(PermissionsService)
    private readonly logger: CmLogger = inject(CM_LOGGER_TEXTURES)
    readonly $editName = signal<boolean>(false)
    private readonly $nameInput = viewChild<ElementRef<HTMLInputElement>>("nameInput")
    $can = this.permission.$to
    readonly queryTextureSetViewData = inject(QueryTextureSetViewDataGQL)
    readonly updateTextureSetName = inject(UpdateTextureSetNameGQL)
    readonly queryInitialTextureRevisions = inject(QueryInitialTextureRevisionsGQL)
    readonly createInitialTextureSetRevision = inject(CreateInitialTextureSetRevisionGQL)
    readonly queryLatestTextureSetRevisionId = inject(QueryLatestTextureSetRevisionIdGQL)
    readonly queryTextureSetJobs = inject(QueryTextureSetJobsGQL)
    readonly utils = inject(UtilsService)
    readonly authService = inject(AuthService)
    private readonly notification = inject(NotificationsService)
    private readonly refreshService = inject(RefreshService)
    private readonly texturesApi = inject(TexturesApiService)
    private readonly matDialog = inject(MatDialog)

    constructor() {
        effect(() => {
            const textureSetId = this.$textureSetId()
            void this.loadTextureSet(textureSetId)
        })
        effect(() => {
            const inputEl = this.$nameInput()?.nativeElement
            if (inputEl) {
                inputEl.focus()
            }
        })
        this.refreshService.itemSubject.pipe(filter(({id}) => id === this.$data()?.textureSetId)).subscribe(({id}) => this.loadTextureSet(id))
    }

    private async loadTextureSet(textureSetId: string | undefined) {
        this.$data.set(undefined)
        if (!textureSetId) {
            return
        }
        const [textureSetRevisionId, textureSetData, textureSetJobs] = await Promise.all([
            this.getOrCreateLatestTextureSetRevision(textureSetId),
            fetchThrowingErrors(this.queryTextureSetViewData)({id: textureSetId}).then((result) => result.textureSet),
            this.$canEdit()
                ? fetchThrowingErrors(this.queryTextureSetJobs)({textureSetId: textureSetId}).then((result) =>
                      result.jobAssignments.filter((x) => x != null).map((x) => x.job),
                  )
                : Promise.resolve([]),
        ])
        this.setData(textureSetData, textureSetJobs, textureSetRevisionId)
        if (this.$activeRevisionJobs().length > 0) {
            this.jobSubmitted.emit()
        }
    }

    protected async changeName($event: Event) {
        const data = this.$data()
        if (!data) {
            throw new Error("Data is not set.")
        }
        const name = ($event.target as HTMLInputElement).value
        this.$data.set({...data, textureSet: {...data.textureSet, name}})
        return this.notification.withUserFeedback(() => mutateThrowingErrors(this.updateTextureSetName)({id: data.textureSet.id, name}), {
            success: "Changes saved.",
            error: "Cannot save changes.",
        })
    }

    protected async deleteTextureSet() {
        const data = this.$data()
        if (!data) {
            return
        }
        if (data.textureSet.textureSetRevisions.length > 0) {
            this.notification.showInfo("Cannot delete non-empty texture set. Delete all revisions first.")
            return
        }
        this.notification
            .confirmationDialog({
                title: "Delete texture set",
                message: `Are you sure you want to delete the entire texture set ?`,
                confirm: "Delete",
                cancel: "Abort",
                isDestructive: true,
            })
            .then(async (confirmed) => {
                if (confirmed) {
                    await this.texturesApi.deleteEmptyTextureSet(data.textureSet.id)
                    this.notification.showInfo("Texture set deleted successfully.")
                }
            })
    }

    protected readonly $activeRevisionJobs = computed(() => {
        const revisions = this.$data()?.textureSet.textureSetRevisions
        if (!revisions) {
            return []
        }
        return revisions
            .map((revision, index) => {
                const job = revision.editsProcessingJob
                if (!job) {
                    return undefined
                }
                const simpleState = getSimpleJobState(job.state)
                if (simpleState === SimpleJobState.Complete || simpleState === SimpleJobState.Failed || simpleState === SimpleJobState.Cancelled) {
                    return undefined
                }
                return {jobId: job.id, label: `(${getRevisionTitle(revision, revisions.length - index)})`}
            })
            .filter(IsDefined)
    })

    readonly $isMaterialAiRunning = computed(
        () => this.jobs?.some((job) => job.name?.includes("Material AI") && getSimpleJobState(job.state) === SimpleJobState.InProgress) ?? false,
    )

    protected onHqProcessingJobCompleted() {
        const data = this.$data()
        if (data) {
            void this.loadTextureSet(data.textureSetId)
        }
        this.jobSubmitted.emit()
    }

    readonly $isEligibleForMaterialAi = computed(() => this.$data()?.textureSetRevision?.eligibleForMaterialAi ?? false)

    protected async startMaterialAiJob() {
        const textureSetRevision = this.$data()?.textureSetRevision
        if (!textureSetRevision) {
            return
        }
        const dialogRef = this.matDialog.open(MaterialAiDialogComponent, {
            width: "800px",
            autoFocus: false,
        })
        dialogRef.afterClosed().subscribe((result) => {
            if (result) {
                this.texturesApi
                    .startMaterialAiJob({
                        textureSetRevisionId: textureSetRevision.id,
                        generateDisplacement: result.generateDisplacement,
                        tags: result.tags,
                        ignoreDpi: result.ignoreDpi,
                        filterRadius: result.filterRadius,
                    })
                    .then(() => {
                        this.refreshService.item(this.$data()?.textureSet)
                        this.notification.showInfo("Material AI job started successfully.")
                    })
            }
        })
    }

    // this is a hack for now which makes sure there exists a texture set revision for the latest texture revisions (because scanning doesn't create them yet)
    private async getOrCreateLatestTextureSetRevision(textureSetId: string | undefined): Promise<string | undefined> {
        if (!textureSetId) {
            return undefined
        }

        const latestTextureSetRevisionId = await fetchThrowingErrors(this.queryLatestTextureSetRevisionId)({textureSetId: textureSetId}).then(
            (result) => result.textureSetRevisions[0]?.id,
        )
        if (latestTextureSetRevisionId) {
            return latestTextureSetRevisionId // we already have a latest texture set revision
        }

        // there are no latest texture set revisions yet; let's create one from the initial texture revisions
        const initialTextureRevisionsResult = await fetchThrowingErrors(this.queryInitialTextureRevisions)({id: textureSetId})
        const initialTextureRevisions = initialTextureRevisionsResult.textureSet.textures
            .filter((texture) => texture.type !== TextureType.Displacement)
            .map((texture) => {
                const initialTextureRevision = texture.revisions.find((revision) => revision.number === 1)
                return initialTextureRevision ? ([texture.type, initialTextureRevision] as const) : undefined
            })
            .filter(IsDefined)
        // we add the displacement map only if it has the same resolution as the other textures (to exclude them if they were generated by auto-tiling)
        const displacementTextureRevision = initialTextureRevisionsResult.textureSet.textures
            .find((texture) => texture.type === TextureType.Displacement)
            ?.revisions.find((revision) => revision.number === 1)
        if (displacementTextureRevision) {
            if (
                displacementTextureRevision.width === initialTextureRevisions[0][1].width &&
                displacementTextureRevision.height === initialTextureRevisions[0][1].height
            ) {
                initialTextureRevisions.push([TextureType.Displacement, displacementTextureRevision] as const)
            }
        }

        if (initialTextureRevisions.length === 0) {
            return undefined // there are no textures to create a texture set revision from
        }

        // let's create a new texture set revision
        this.logger.info("Creating initial texture set revision for revisions: ", initialTextureRevisions)
        const initialCreationDate = initialTextureRevisions.map((revision) => new Date(revision[1].createdAt)).sort((a, b) => a.getTime() - b.getTime())[0]
        const createInitialTextureSetRevisionResult = await mutateThrowingErrors(this.createInitialTextureSetRevision)({
            input: {
                textureSetId: textureSetId,
                createdAt: initialCreationDate?.toISOString(),
                createdBy: initialTextureRevisionsResult?.textureSet.scanJob?.createdBy?.id,
                width: initialTextureRevisions[0]?.[1].width ?? 30,
                height: initialTextureRevisions[0]?.[1].height ?? 30,
                displacement: initialTextureRevisions.find((revision) => revision[0] === TextureType.Displacement)?.[1].displacement,
                mapAssignments: initialTextureRevisions.map((revision) => ({
                    textureType: revision[0],
                    dataObjectId: revision[1].dataObject.id,
                })),
            },
        })
        return createInitialTextureSetRevisionResult.createTextureSetRevision.id
    }

    protected onSelectRevision(textureSetRevisionId: string) {
        const data = this.$data()
        if (!data) {
            return
        }
        this.setData(data.textureSet, this.jobs, textureSetRevisionId)
    }

    private setData(textureSet: TextureSetViewDataFragment, jobs: TextureSetJobFragment[] | undefined, textureSetRevisionId: string | undefined) {
        this.$data.set({
            textureSetId: textureSet.id,
            textureSetRevisionId: textureSetRevisionId,
            textureSet: textureSet,
            textureSetRevision: textureSet.textureSetRevisions.find((revision) => revision.id === textureSetRevisionId),
        })
        this.jobs = jobs
    }

    // deleteTextureRevision(set: TextureSet, texture: Texture) {
    //     const dialogRef: MatDialogRef<DialogComponent> = this.dialog.open(DialogComponent, {
    //         disableClose: false,
    //         width: "400px",
    //         data: {
    //             title: "Delete revision",
    //             message:
    //                 "The latest texture revision will be deleted. " +
    //                 "This action <strong>cannot be undone</strong>.<br><br>Are you sure you want to continue?",
    //             confirmLabel: "Delete revision",
    //             cancelLabel: "Cancel",
    //         },
    //     })
    //     dialogRef
    //         .afterClosed()
    //         .pipe(takeUntil(this.unsubscribe))
    //         .subscribe((confirmed) => {
    //             if (!confirmed) return
    //             set.deleteLatestTextureRevision(texture).subscribe({
    //                 next: () => this.snackBar.open("Revision deleted successfully.", "", {duration: 3000}),
    //                 error: () =>
    //                     this.snackBar.open("Cannot delete revision. Probably there is a material revision using it. Delete that one first.", "", {
    //                         duration: 3000,
    //                     }),
    //             })
    //         })
    // }

    readonly $textureSetLabel = computed(() => {
        const data = this.$data()
        if (!data) {
            return ""
        }
        const name = data.textureSet.name ?? "Untitled"
        const revision = data.textureSetRevision
        const dimensions = revision ? ` (${revision.width.toFixed(2)} × ${revision.height.toFixed(2)} cm)` : ""
        return `${data.textureSet.legacyId} - ${name}${dimensions}`
    })

    readonly $showEmptyTextures = signal<boolean | undefined>(undefined)
    readonly $data = signal<
        | {
              textureSetId: string
              textureSetRevisionId: string | undefined
              textureSet: TextureSetViewDataFragment
              textureSetRevision?: TextureSetViewRevisionDataFragment
          }
        | undefined
    >(undefined)
    protected jobs?: TextureSetJobFragment[]
    protected readonly getRevisionTitle = getRevisionTitle
}
